/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed, set } from '@ember/object';
import { service } from '@ember/service';
import DS from 'ember-data';
import { or } from '@ember/object/computed';
import { isNone, isPresent } from '@ember/utils';
import colorLabels from 'cio/utils/color-labels';
import TemplateModel from 'cio/models/template';
import ActionModel from 'cio/models/action';
import CampaignModel from 'cio/models/campaign';
import NewsletterModel from 'cio/models/newsletter';
import TransactionalMessageModel from 'cio/models/transactional-message';
import { getDefaultTemplates } from 'cio/utils/newsletter-testgroups';
import { EditorNameMap } from 'cio/services/editor';

type NameModel = TemplateModel | ActionModel;
type NameModels = TemplateModel[] | ActionModel[];

interface ActionComposerHeaderArgs {
  isDirty: boolean;
  nameModel: NameModel;
  namePlaceholder: string;
  campaign?: CampaignModel;
  newsletter?: NewsletterModel;
  transactionalMessage?: TransactionalMessageModel;
  parentRoute: string;
  saveModels: NameModels;
  doneLinkText: string;
  doneLinkPrefix?: string;
  doneRoute: string;
  hasReadOnlyPermission: boolean;
}

export default class ActionComposerHeader extends Component<ActionComposerHeaderArgs> {
  @service store!: DS.Store;

  get saveModels() {
    return this.args.saveModels ?? [];
  }

  get doneLinkPrefix() {
    return this.args.doneLinkPrefix ?? 'Back to';
  }

  @computed('saveModels.@each.isSaving')
  get isAnySaveModelSaving() {
    return this.saveModels.some((model: NameModel) => model.isSaving);
  }

  @or('args.campaign', 'args.newsletter', 'args.transactionalMessage') parentModel!:
    | CampaignModel
    | NewsletterModel
    | TransactionalMessageModel;

  @computed('args.doneRoute', 'parentModel.id', 'args.nameModel.id', 'args.newsletter')
  get doneRouteParams() {
    if (!isNone(this.args.newsletter)) {
      return {};
    }

    return {
      actionId: this.args.nameModel.id,
    };
  }

  @computed('args.nameModel.templateType')
  get isEmail() {
    if (this.args.nameModel instanceof TemplateModel) {
      return this.args.nameModel.templateType === 'email';
    } else {
      return this.args.nameModel.isEmail;
    }
  }

  get allowEmptyName() {
    if (this.args.nameModel instanceof ActionModel) {
      return !this.args.nameModel.info.nameRequired ?? true;
    }

    return true;
  }

  @computed('args.campaign', 'args.newsletter', 'args.nameModel')
  get isSplitVariation() {
    if (!isNone(this.args.campaign) && this.args.nameModel instanceof ActionModel) {
      return this.args.nameModel.parentAction.get('isSplitTest');
    } else if (!isNone(this.args.newsletter) && this.args.nameModel instanceof TemplateModel) {
      return this.args.nameModel.newsletter.splitTestActive;
    } else if (!isNone(this.args.transactionalMessage) && this.args.nameModel instanceof TemplateModel) {
      return false; // transactional messages do not support split variations
    }

    return false;
  }

  @computed('isSplitVariation', 'args.campaign', 'args.newsletter', 'args.nameModel')
  get splits() {
    let splits: NameModel[] = [];

    if (!this.isSplitVariation) {
      return splits;
    }

    if (!isNone(this.args.campaign) && this.args.nameModel instanceof ActionModel) {
      splits = [...this.args.nameModel.parentAction.get('actions')] ?? [];
    } else if (!isNone(this.args.newsletter) && this.args.nameModel instanceof TemplateModel) {
      return [...this.args.nameModel.newsletter.get('activeTemplates')] || [];
    }

    return splits;
  }

  @computed('splits', 'args.nameModel')
  get splitBadge() {
    let splits = [...this.splits];

    if (!isNone(this.args.newsletter) && this.args.nameModel instanceof TemplateModel) {
      splits = getDefaultTemplates(this.splits as TemplateModel[]);
    }

    let index = splits.findIndex((split) => {
      if (
        this.args.nameModel instanceof TemplateModel &&
        split instanceof TemplateModel &&
        this.args.nameModel.testGroupId != null &&
        split.testGroupId != null
      ) {
        return split.testGroupId === this.args.nameModel.testGroupId;
      }

      return split.id === this.args.nameModel.id;
    });

    if (index === -1) {
      return null;
    }

    let { label, color } = colorLabels[index];

    return {
      label,
      color,
    };
  }

  get isUpdatedByUserInfoAvailable() {
    return 'updatedByUser' in this.parentModel;
  }

  get template(): TemplateModel | null {
    if (this.args.nameModel instanceof TemplateModel) {
      return this.args.nameModel;
    }

    if (this.args.nameModel instanceof ActionModel && this.args.nameModel.templateId) {
      return this.store.peekRecord('template', this.args.nameModel.templateId);
    }

    return null;
  }

  get saveStatusMetadata(): Record<string, string> {
    let metadata: Record<string, string> = {};

    if (this.args.nameModel instanceof ActionModel) {
      metadata['Action ID'] = this.args.nameModel.id;
    } else if (this.args.newsletter) {
      metadata['Newsletter ID'] = this.args.newsletter.id;
    } else if (this.args.transactionalMessage) {
      metadata['Transactional ID'] = this.args.transactionalMessage.id;
    }

    if (this.template != null) {
      let editorName = EditorNameMap[this.template.editor];
      if (isPresent(editorName)) {
        metadata.Editor = editorName;
      }

      metadata['Liquid Version'] = this.template.templateEngineName;
    }

    return metadata;
  }

  @action
  saveName(name: string) {
    set(this.args.nameModel, 'name', name);
    return this.args.nameModel.save();
  }
}
